/**
 * Environment Switcher Component
 * 
 * Allows users to switch between Production and Staging environments
 */

import { useState, useEffect, useRef } from 'react'
import {
  getCurrentEnvironment,
  setCurrentEnvironment,
  subscribeToEnvironment,
  ENVIRONMENT_INFO,
  type Environment
} from '../services/environmentStore'
import './EnvironmentSwitcher.css'

interface EnvironmentSwitcherProps {
  onEnvironmentChange?: (env: Environment) => void
}

export default function EnvironmentSwitcher({ onEnvironmentChange }: EnvironmentSwitcherProps) {
  const [environment, setEnvironment] = useState<Environment>(getCurrentEnvironment())
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Subscribe to environment changes
  useEffect(() => {
    const unsubscribe = subscribeToEnvironment((env) => {
      setEnvironment(env)
    })
    return unsubscribe
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (env: Environment) => {
    if (env !== environment) {
      setCurrentEnvironment(env)
      onEnvironmentChange?.(env)
      
      // Reload the page to apply new environment
      window.location.reload()
    }
    setIsOpen(false)
  }

  const currentInfo = ENVIRONMENT_INFO[environment]

  return (
    <div className="env-switcher" ref={dropdownRef}>
      <button
        className={`env-switcher-btn env-${environment}`}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          backgroundColor: currentInfo.bgColor,
          borderColor: currentInfo.color
        }}
      >
        {environment === 'staging' ? (
          <span className="env-icon material-symbols-rounded" style={{ color: currentInfo.color }}>
            science
          </span>
        ) : (
          <span className="env-icon material-symbols-rounded" style={{ color: currentInfo.color }}>
            public
          </span>
        )}
        <span className="env-label">{currentInfo.label}</span>
        <span className="material-symbols-rounded env-arrow">
          {isOpen ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {isOpen && (
        <div className="env-dropdown">
          {(Object.keys(ENVIRONMENT_INFO) as Environment[]).map((env) => {
            const info = ENVIRONMENT_INFO[env]
            const isActive = env === environment

            return (
              <button
                key={env}
                className={`env-option ${isActive ? 'active' : ''}`}
                onClick={() => handleSelect(env)}
              >
                {env === 'staging' ? (
                  <span className="env-icon material-symbols-rounded" style={{ color: info.color }}>
                    science
                  </span>
                ) : (
                  <span className="env-icon material-symbols-rounded" style={{ color: info.color }}>
                    public
                  </span>
                )}
                <span className="env-option-label">{info.label}</span>
                {isActive && (
                  <span className="material-symbols-rounded env-check">check</span>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
